import { _ } from "../../../polyglot/polyglot.js";
/**
 * This file exports a few helper functions to create footer builders
 * for NewDialog.js.
 */

const configuration = {
	delay: 2000,
};

/**
 * Configuration for `makeCancelAndDeleteFooterBuilder`.
 *
 * @param {number} [delay] - Number of milliseconds the notification
 * will stay visible.
 */
export function setConfiguration({ delay } = {}) {
	configuration.delay = delay;
}

/**
 * Render a list of buttons in the footer.
 *
 * @param {object} options - Object parameters
 * @param {Array<function>} options.mainButtons - List of main buttons
 * @param {Array<function>} options.extraButtons - List of extra buttons
 * @return {Renderable}
 */
export function footerButtonsRenderer({ mainButtons = [], extraButtons = [] }) {
	return (html) => {
		if (extraButtons.length)
			html.ul(
				{ class: "list-inline pull-left" },
				extraButtons.map((button) => (html) => html.li(button)),
			);

		if (mainButtons.length)
			html.ul(
				{ class: "list-inline pull-right" },
				mainButtons.map((button) => (html) => html.li(button)),
			);
	};
}

/**
 * Render a cancel link.
 *
 * @param {object} options - Object parameters
 * @param {function} options.hide - Function to hide the dialog
 * @param {string} [options.cancelLabel] - Text of the cancel link
 * @param {function} [options.onCancel] - Function to call when the link is
 *   clicked
 * @return {Renderable}
 */
export function cancelLinkRenderer({
	hide,
	cancelLabel = _("Cancel"),
	onCancel = () => {},
}) {
	return (html) =>
		html.a(
			{
				click: () => {
					hide();
					onCancel();
				},
				"data-test": "cancel",
			},
			cancelLabel,
		);
}

/**
 * Just one accept button. This is useful for information dialogs.
 */
export function makeOkFooterBuilder({
	okLabel = _("Ok"),
	callback = () => {},
} = {}) {
	return ({ hide }) =>
		(html) =>
			html.button(
				{
					class: "btn btn-primary",
					"data-test": "ok",
					click: () => {
						hide();
						callback();
					},
				},
				okLabel,
			);
}

/**
 * Shows both a cancel and an ok button.
 */
export function makeCancelAndOkFooterBuilder({
	okLabel = _("Ok"),
	cancelLabel = _("Cancel"),
	extraButtonsRenderer,
	callback,
} = {}) {
	return ({ hide }) =>
		footerButtonsRenderer({
			mainButtons: [
				cancelLinkRenderer({ hide, cancelLabel }),
				(html) =>
					html.button(
						{
							class: "btn btn-primary",
							click: () => callback({ hide }),
							"data-test": "ok",
						},
						okLabel,
					),
			],
			extraButtons: extraButtonsRenderer?.({ hide }),
		});
}

/**
 * Shows both a cancel and a delete button. The delete button is only
 * enabled after a delay of `millisecondsBeforeActivation`.
 */
export function makeCancelAndDeleteFooterBuilder({
	okLabel = _("Delete"),
	cancelLabel,
	callback = () => {},
	onCancel,
	millisecondsBeforeActivation = configuration.delay,
} = {}) {
	return ({ hide }) =>
		footerButtonsRenderer({
			mainButtons: [
				cancelLinkRenderer({ hide, cancelLabel, onCancel }),
				(html) => {
					const button = html.button(
						{
							class: "btn btn-danger",
							"data-test": "ok",
							click: () => {
								hide();
								callback();
							},
							disabled: !!millisecondsBeforeActivation,
						},
						okLabel,
					);

					setTimeout(() => {
						button.asJQuery().removeAttr("disabled");
					}, millisecondsBeforeActivation);
				},
			],
		});
}
